import {
  useEffect,
  useRef,
  useState,
} from "react";

import type {
  MrzScanner,
} from "./mrzScanner";
import {
  ScanAccumulator,
} from "./scanAccumulator";

// Frames are captured at most this wide before OCR — a full 4K frame buys
// nothing for the MRZ and costs seconds per attempt on a phone. The scanner
// crops the MRZ band from these SOURCE pixels, so this cap is what bounds the
// glyph detail OCR actually sees — don't lower it below ~1920.
const CAPTURE_MAX_WIDTH = 1920;
// Breather between attempts so the phone can render and refocus.
const ATTEMPT_PAUSE_MS = 250;

interface CameraScannerProps {
  scanner: () => Promise<MrzScanner>;
  onFound: (values: Record<string, string>) => void;
  /** Camera unavailable / permission denied — caller falls back to upload. */
  onUnavailable: (message: string) => void;
  onCancel: () => void;
}

function captureFrame(video: HTMLVideoElement): Promise<Blob | null> {
  if (video.videoWidth === 0) return Promise.resolve(null);
  const scale = Math.min(1, CAPTURE_MAX_WIDTH / video.videoWidth);
  const canvas = document.createElement("canvas");
  canvas.width = Math.round(video.videoWidth * scale);
  canvas.height = Math.round(video.videoHeight * scale);
  const ctx = canvas.getContext("2d");
  if (ctx === null) return Promise.resolve(null);
  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
  return new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/png"),
  );
}

/**
 * Live passport scanning: streams the rear camera into a viewfinder and keeps
 * OCR-ing frames on-device until an MRZ validates — no shutter to press. The
 * stream never leaves the browser; frames are handed straight to the local
 * OCR worker and discarded.
 */
export function CameraScanner({
  scanner,
  onFound,
  onUnavailable,
  onCancel,
}: CameraScannerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [attempts, setAttempts] = useState(0);
  const [fieldsFound, setFieldsFound] = useState(0);

  // Latest-callback refs so the mount effect below can have genuinely empty
  // dependencies — re-running it would tear down and re-prompt for the camera.
  const callbacksRef = useRef({ scanner, onFound, onUnavailable });
  callbacksRef.current = { scanner, onFound, onUnavailable };

  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | null = null;

    const run = async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: "environment",
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
          audio: false,
        });
      } catch {
        if (!cancelled) {
          callbacksRef.current.onUnavailable(
            "Couldn't open the camera — you can upload a photo instead.",
          );
        }
        return;
      }
      const video = videoRef.current;
      if (cancelled || video === null) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      // A worker-boot or OCR failure must not strand the viewfinder on "hold
      // steady" — the one state indistinguishable from progress. Route it to
      // the upload fallback instead.
      try {
        video.srcObject = stream;
        try {
          // play() can THROW synchronously (jsdom) as well as reject
          // (autoplay policies); a muted inline stream generally renders
          // regardless, so a play failure isn't fatal.
          await video.play();
        } catch {
          /* keep scanning */
        }
        const mrz = await callbacksRef.current.scanner();
        // Readings merge across frames: check-digit-backed fields lock in as
        // they validate, names need two agreeing frames (they have no check
        // digit), and a settle deadline releases the best partial rather than
        // scanning forever — see ScanAccumulator.
        const accumulator = new ScanAccumulator();
        while (!cancelled) {
          const frame = await captureFrame(video);
          if (cancelled) return;
          if (frame !== null) {
            // The viewfinder guide makes the full-image fallback redundant,
            // and skipping it halves per-frame OCR cost on a phone.
            const values = await mrz.scan(frame, { bandOnly: true });
            if (cancelled) return;
            accumulator.addFrame(values ?? {}, Date.now());
            const result = accumulator.result(Date.now());
            setFieldsFound(result.fieldsFound);
            if (result.done && result.fieldsFound > 0) {
              callbacksRef.current.onFound(result.values);
              return;
            }
            setAttempts((n) => n + 1);
          }
          await new Promise((resolve) =>
            setTimeout(resolve, ATTEMPT_PAUSE_MS),
          );
        }
      } catch (err) {
        console.error("Live passport scan failed", err);
        if (!cancelled) {
          callbacksRef.current.onUnavailable(
            "Scanning hit a snag — you can upload a photo instead.",
          );
        }
      }
    };

    void run();
    return () => {
      cancelled = true;
      stream?.getTracks().forEach((track) => track.stop());
    };
  }, []);

  return (
    <div>
      <div className="relative overflow-hidden rounded-md bg-black">
        <video
          ref={videoRef}
          playsInline
          muted
          autoPlay
          className="block w-full"
          aria-label="Camera viewfinder for passport scanning"
        />
        {/* Framing guide: passport photo page proportions, MRZ zone marked */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="relative h-[70%] w-[88%] rounded-lg border-2 border-white/80">
            <div className="absolute inset-x-2 bottom-2 h-1/4 rounded border border-dashed border-white/70" />
          </div>
        </div>
        <p className="absolute inset-x-0 bottom-1 text-center text-xs text-white/90">
          {fieldsFound > 0
            ? `Reading… ${fieldsFound} of 5 locked — hold steady`
            : attempts === 0
              ? "Hold the photo page inside the frame"
              : "Looking for the code lines… hold steady"}
        </p>
      </div>
      <div className="mt-2 flex items-center justify-between">
        <p className="text-xs text-gray-500" role="status">
          Scanning live on your device — nothing is uploaded.
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="text-sm font-medium text-gray-600 hover:text-gray-800"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
